import { useState, useMemo } from 'react';
import { useSwrStaticPhoneCountries } from '@hooks/useSwrApi/endpoints/static';
import { useDebounce } from '@hooks/useDebounce/useDebounce';
import { useSwrUserPhoneNumber } from '@hooks/useSwrApi/endpoints/user/useSwrUserPhoneNumber';
import { useSwrUserPhoneVerify } from '@hooks/useSwrApi/endpoints/user/useSwrUserPhoneVerify';
import { useSwrUserPhoneInfo } from '@hooks/useSwrApi/endpoints/user/useSwrUserPhoneInfo';
import { useSwrUserPhoneConfirm } from '@hooks/useSwrApi/endpoints/user/useSwrUserPhoneConfirm';

/** Props */
interface UsePhoneNumberFlowProps {
  locale: Locale;
  startFromConfirm?: boolean;
  successSavePhoneCallback?: (response?: ApiSchema<'PhoneNumberInfo'>) => void;
  successConfirmPhoneCallback?: () => void;
}

/**
 * @description Phone number flow hook.
 * - `locale` - The locale.
 * - `startFromConfirm` - Start from the phone confirm step.
 * - `successSavePhoneCallback` - The success callback for saving the phone number.
 * - `successConfirmPhoneCallback` - The success callback for confirming the phone number.
 */
export const usePhoneNumberFlow = (props: UsePhoneNumberFlowProps) => {
  // Destructure props
  const {
    locale,
    startFromConfirm = false,
    successSavePhoneCallback,
    successConfirmPhoneCallback,
  } = props;

  // SWR phone prefixes
  const {
    data: phonePrefixes,
  } = useSwrStaticPhoneCountries(locale);

  // Selected country code
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    phonePrefixes?.find((item) => item.id === 'RO')?.name || '40',
  );

  // Customer phone number
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  // SWR get user phone number (needed if we start from confirm)
  const {
    data: apiPhoneNumber,
  } = useSwrUserPhoneNumber({
    locale,
    shouldFetch: startFromConfirm,
    successCallback: (response) => {
      const responseData = response?.data;
      const responseCountryCode = responseData?.country?.data?.countryCode;
      const responseNumber = responseData?.number;
      if (responseCountryCode) setSelectedCountryCode(responseCountryCode);
      if (responseNumber) setPhoneNumber(responseNumber);
    },
  });

  // Phone number validity states
  const [phoneValidityStates, setPhoneValidityStates] = useState({ isInvalid: false, errorMSG: '' });
  const [smsCodeValidityStates, setSmsCodeValidityStates] = useState({ isInvalid: false, errorMSG: '' });

  // Number already in use
  const [numberAlreadyInUse, setNumberAlreadyInUse] = useState<boolean>(false);

  // Merged phone number (country code + phone number)
  const mergedPhoneNumber = useMemo(
    () => `+${selectedCountryCode}${phoneNumber}`,
    [selectedCountryCode, phoneNumber],
  );

  // Show confirm step
  const [showConfirmStep, setShowConfirmStep] = useState<boolean>(startFromConfirm);

  // Confirm SMS code
  const [confirmSmsCode, setConfirmSmsCode] = useState<string>('');


  // SWR user phone verify
  // ************************
  const {
    trigger: triggerSendSms,
    isMutating: isSendingSms,
  } = useSwrUserPhoneVerify({
    locale,
    successCallback: () => {
      setShowConfirmStep(true);
      setNumberAlreadyInUse(false);
    },
  });

  // Debounce verify user phone
  const sendSMSToUser = useDebounce(() => {
    if (mergedPhoneNumber.length >= 7 && mergedPhoneNumber.length <= 12) {
      void triggerSendSms({
        number: mergedPhoneNumber,
      });
    }
  }, 200);


  // SWR user phone info
  // ************************
  const {
    trigger: triggerUserPhoneInfo,
    isMutating: isUserPhoneInfoMutating,
  } = useSwrUserPhoneInfo({
    locale,
    successCallback: (response) => {
      // phone number is not used and send a confirmation code (SMS)
      if (!response?.isConfirmed) {
        sendSMSToUser();
      }
      // phone number is already in use
      if (response?.isAlreadyInUse) setNumberAlreadyInUse(true);
      // External callback
      if (successSavePhoneCallback) successSavePhoneCallback(response);
    },
  });

  // Debounce verify user phone
  const verifyUserPhoneInfo = useDebounce(() => {
    if (mergedPhoneNumber.length >= 7 && mergedPhoneNumber.length <= 12) {
      void triggerUserPhoneInfo({
        number: mergedPhoneNumber,
      });
    }
  }, 200);


  // SWR user phone confirm
  // ************************
  const {
    trigger: triggerUserPhoneConfirm,
    isMutating: isUserPhoneConfirmMutating,
  } = useSwrUserPhoneConfirm({
    locale,
    successCallback: () => {
      if (successConfirmPhoneCallback) successConfirmPhoneCallback();
    },
  });

  // Debounce confirm user phone
  const confirmUserPhone = useDebounce(() => {
    if (confirmSmsCode.length === 4) {
      void triggerUserPhoneConfirm({
        phoneNumber: {
          number: mergedPhoneNumber,
        },
        code: confirmSmsCode,
      });
    }
  }, 200);


  // Return values
  // **********************
  return {
    phonePrefixes,
    selectedCountryCode,
    setSelectedCountryCode,
    apiPhoneNumber,
    phoneNumber,
    setPhoneNumber,
    phoneValidityStates,
    setPhoneValidityStates,
    mergedPhoneNumber,
    numberAlreadyInUse,
    sendSMSToUser,
    isSendingSms,
    isUserPhoneInfoMutating,
    verifyUserPhoneInfo,
    showConfirmStep,
    confirmSmsCode,
    setConfirmSmsCode,
    confirmUserPhone,
    isUserPhoneConfirmMutating,
    smsCodeValidityStates,
    setSmsCodeValidityStates,
  };
};
